import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useAuthStore } from "@/stores/auth";
import CustomerHomePage from "@/views/CustomerHomePage.vue";
import CustomerHealthReportPage from "@/views/customer/CustomerHealthReportPage.vue";
import CustomerHistoryPage from "@/views/customer/CustomerHistoryPage.vue";
import CustomerProfilePage from "@/views/customer/CustomerProfilePage.vue";
import CustomerVaccinePage from "@/views/customer/CustomerVaccinePage.vue";

const routes: RouteRecordRaw[] = [
  { path: "/", redirect: "/login" },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginPage.vue"),
    meta: { public: true },
  },
  {
    path: "/main",
    name: "main",
    component: () => import("@/views/MainPage.vue"),
  },
  {
    path: "/staff/admin",
    name: "staff-admin",
    component: () => import("@/views/StaffDashboardPage.vue"),
    meta: { roles: ["ADMIN", "SUPERADMIN"] },
  },
  {
    path: "/staff/nurse",
    name: "staff-nurse",
    component: () => import("@/views/StaffDashboardPage.vue"),
    meta: { roles: ["NURSE", "ADMIN", "SUPERADMIN"] },
  },
  {
    path: "/staff/mkt",
    name: "staff-mkt",
    component: () => import("@/views/StaffDashboardPage.vue"),
    meta: { roles: ["MARKETING", "MKT", "ADMIN", "SUPERADMIN"] },
  },
  {
    path: "/staff/telemed-room/:appointmentId",
    name: "staff-telemed-room",
    component: () => import("@/views/staff/StaffTelemedRoomPage.vue"),
    meta: { roles: ["DOCTOR", "NURSE", "ADMIN", "SUPERADMIN"] },
  },

  {
    // Accepts both /register/<key> (legacy path) and /register?contractKey=<key>
    // (LIFF query — LIFF Endpoint URL stays at /register so a single LIFF app
    // works for any number of contracts).
    path: "/register/:contractKey?",
    name: "customer-register",
    component: () => import("@/views/CustomerRegisterPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/home",
    name: "customer-home",
    component: CustomerHomePage,
    meta: { public: true },
  },
  {
    path: "/customer/family",
    name: "customer-family",
    component: () => import("@/views/customer/CustomerFamilyPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/profile",
    name: "customer-profile",
    component: CustomerProfilePage,
    meta: { public: true },
  },
  {
    path: "/customer/vaccine",
    name: "customer-vaccine",
    component: CustomerVaccinePage,
    meta: { public: true },
  },
  {
    path: "/customer/vaccine/booking",
    name: "customer-vaccine-booking",
    component: () => import("@/views/customer/CustomerVaccineBookingPage.vue"),
    meta: { public: true },
  },

  {
    path: "/customer/receipt",
    name: "customer-receipt",
    component: () => import("@/views/customer/CustomerReceiptPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/history",
    name: "customer-history",
    component: CustomerHistoryPage,
    meta: { public: true },
  },
  {
    path: "/customer/history/visit-lab/:orderId",
    name: "customer-visit-lab-detail",
    component: () => import("@/views/customer/CustomerVisitLabDetailPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/appointment",
    name: "customer-appointment",
    component: () => import("@/views/customer/CustomerAppointmentPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/profile/health",
    name: "customer-profile-health",
    component: () => import("@/views/customer/CustomerHealthEditPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/doctor/:hospId/:usrid",
    name: "customer-doctor-profile",
    component: () => import("@/views/customer/CustomerDoctorProfilePage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/telemed-room/:appointmentId",
    name: "customer-telemed-room",
    component: () => import("@/views/customer/CustomerTelemedRoomPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/notification",
    name: "customer-notification",
    component: () => import("@/views/customer/NotificationCenterPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/notification/settings",
    name: "customer-notification-settings",
    component: () => import("@/views/customer/NotificationSettingsPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/health-report",
    name: "customer-health-report",
    component: CustomerHealthReportPage,
    meta: { public: true },
  },
  {
    path: "/customer/health-report/category/:type",
    name: "customer-health-category",
    component: () => import("@/views/customer/CustomerHealthCategoryPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/health-report/test/:id",
    name: "customer-health-test",
    component: () => import("@/views/customer/CustomerHealthTestPage.vue"),
    meta: { public: true },
  },

  {
    path: "/customer/health-report/full-summary",
    name: "customer-health-full-summary",
    component: () =>
      import("@/views/customer/CustomerHealthFullSummaryPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/health-report/history",
    name: "customer-health-history",
    component: () => import("@/views/customer/CustomerHealthHistoryPage.vue"),
    meta: { public: true },
  },
  {
    path: "/customer/promotion",
    name: "customer-promotion",
    component: () => import("@/views/customer/CustomerPromotionPage.vue"),
    meta: { public: true },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFoundPage.vue"),
    meta: { public: true },
  },
];

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.ready) await auth.bootstrap();
  if (to.meta.public) return true;
  if (!auth.isAuthenticated)
    return { name: "login", query: { next: to.fullPath } };
  const requiredRoles = (to.meta.roles as string[] | undefined) ?? [];
  if (
    requiredRoles.length &&
    auth.role &&
    !requiredRoles.includes(auth.role) &&
    auth.role !== "SUPERADMIN"
  ) {
    return { name: "main" };
  }
  // We removed the automatic redirect from /main to role-specific dashboards
  // so that all roles can view the Patient Queue (Main Dashboard) if they want,
  // and navigate to their specific dashboards via the new Sidebar Menu.

  return true;
});

router.onError((error, to) => {
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Failed to load module script") ||
    error.name === "ChunkLoadError"
  ) {
    if (!to.fullPath.includes("chunkLoad=")) {
      const separator = to.fullPath.includes("?") ? "&" : "?";
      window.location.href = `${to.fullPath}${separator}chunkLoad=${Date.now()}`;
    }
  }
});
